import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import isEqual from "lodash/isEqual";
import { moveArrayItem } from "@p81-common/shell-core-js";

function defaultCreateTableSettings(config, minSettingsListItems) {
  return config.slice(1).map((item, index, array) => ({
    checkboxProps: { disabled: array.length <= minSettingsListItems },
    name: item.headerName,
    selected: item.show,
  }));
}

// Attach the stable `field` to each settings entry by resolving it against the
// config at the SAME render the settings were built from. `name` (headerName) and
// the config's headerName come from the same t() call, so this mapping is correct
// even before i18n loads (both are raw keys). Matching by `field` afterwards is
// immune to headerName changing once translations arrive. (P81-131187)
function withResolvedFields(settings, config) {
  return settings.map((item) => ({
    ...item,
    field: item.field ?? config.find((c) => c.headerName === item.name)?.field,
  }));
}

function useTableSettings(options) {
  const {
    initialTableConfig,
    createTableSettings = defaultCreateTableSettings,
    minSettingsListItems,
  } = options;

  const initialTableSettings = useMemo(
    () =>
      withResolvedFields(
        createTableSettings(initialTableConfig, minSettingsListItems),
        initialTableConfig
      ),
    [createTableSettings, initialTableConfig, minSettingsListItems]
  );

  return {
    initialTableSettings,
    initialTableConfig,
  };
}

export function useDataGrid(options = {}) {
  const { minSettingsListItems = -1 } = options;

  const { initialTableSettings, initialTableConfig } = useTableSettings(
    options
  );

  const [tableSettings, setTableSettings] = useState(initialTableSettings);

  // Keep the settings labels in sync with the current (translated) config.
  // tableSettings is snapshotted on first render and may hold raw i18n keys as
  // `name` if the grid mounted before its namespace loaded. Refresh `name` from
  // the current config (matched by the stable `field`) so the column-settings
  // menu shows real labels and the "modified" dot doesn't false-trigger.
  // Selection and order are preserved. (P81-131187)
  useEffect(() => {
    setTableSettings((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        const col = initialTableConfig.find((c) => c.field === item.field);
        if (col && col.headerName !== item.name) {
          changed = true;
          return { ...item, name: col.headerName };
        }
        return item;
      });
      return changed ? next : prev;
    });
  }, [initialTableConfig]);

  const onTableSettingsMenuItemClick = useCallback(
    (value) => () => {
      let selectedCount = 0;
      const result = tableSettings
        .map((item) => {
          const newItem =
            item.name === value
              ? {
                  ...item,
                  selected: !item.selected,
                }
              : item;

          if (newItem.selected) {
            selectedCount++;
          }

          return newItem;
        })
        .map((item) => ({
          ...item,
          checkboxProps: {
            ...item.checkboxProps,
            disabled: isCheckboxDisabled(item, selectedCount),
          },
        }));
      setTableSettings(result);
    },
    [minSettingsListItems, tableSettings]
  );

  const onTableSettingsSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      if (oldIndex !== newIndex) {
        const newArray = moveArrayItem(tableSettings, oldIndex, newIndex);
        setTableSettings(newArray);
      }
    },
    [tableSettings]
  );

  const tableSettingsChanged = useMemo(
    () => !isEqual(initialTableSettings, tableSettings),
    [tableSettings, initialTableSettings]
  );

  const columnWidthCache = useRef({});

  const tableConfig = useMemo(() => {
    function setColumnWidth(item) {
      const currentWidth = columnWidthCache.current[item.field];
      return currentWidth
        ? {
            ...item,
            width: currentWidth,
          }
        : item;
    }

    const result = [
      setColumnWidth(initialTableConfig[0]),
      ...tableSettings.reduce((acc, item) => {
        if (item.selected) {
          const newItem = initialTableConfig.find((i) =>
            item.field != null
              ? i.field === item.field
              : i.headerName === item.name
          );
          if (newItem) {
            return acc.concat(setColumnWidth(newItem));
          }
        }
        return acc;
      }, []),
    ];
    if (initialTableConfig.at(-1).type === "actionButtons") {
      result.push(initialTableConfig.at(-1));
    }
    return result;
  }, [tableSettings, initialTableConfig]);

  const onColumnWidthChange = useCallback(({ width, colDef }) => {
    columnWidthCache.current[colDef.field] = width;
  }, []);

  const isCheckboxDisabled = useCallback(
    (item, selectedCount) => {
      if (minSettingsListItems < 0) return item.checkboxProps.disabled;
      return selectedCount <= minSettingsListItems ? !!item.selected : false;
    },
    [minSettingsListItems]
  );

  return {
    tableConfig,
    tableSettings,
    tableSettingsChanged,
    onColumnWidthChange,
    onTableSettingsMenuItemClick,
    onTableSettingsSortEnd,
  };
}
